import { Box, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import {
  DateInput,
  Edit,
  EditProps,
  FormGroupContextProvider,
  FormWithRedirect,
  required,
  useNotify,
  useRedirect,
  useUpdate,
} from "react-admin";
import { useSelector } from "react-redux";

import CustomCard from "../../components/CustomCard";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { ReduxState } from "../../types/redux.types";
import { AppResource } from "../../types/resource.types";
import { applyUpdateTimestamp, generateTimestamp } from "../../utils/timestamp";
import { isDateIsOrAfterToday, validateFileSize } from "../../utils/validators";
import { LinkType } from "./types";
import { useIsValidAdRecord, useIsValidExternalLink } from "./utils";

const CustomForm: FC = (props) => {
  const ad = (props as any).record;
  const [linkType, setLinkType] = useState<LinkType>(LinkType.internal);
  const [recordId, setRecordId] = useState<string>("");
  const [externalLink, setExternalLink] = useState<string>("");
  const settings = useSelector<ReduxState, any>((state) => state.settings);

  const [internalType, setInternalType] = useState(
    ad.type === AppResource.sportsClubs ? "club" : ad.type === "store" ? "store" : "place"
  );

  const handleChangeInternalType = (event: any) => {
    setInternalType(event.target.value);
  };

  const [update, { loading }] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const { disabledStyles } = useDisabledOnLoading(loading);

  const {
    error: adError,
    isValidAdRecord,
    adRecord,
    adDeepUrl,
  } = useIsValidAdRecord(
    recordId,
    internalType === "club"
      ? AppResource.sportsClubs
      : internalType === "store"
      ? AppResource.brands
      : AppResource.places
  );
  const { isValidExternalLink, error: externalLinkError } = useIsValidExternalLink(externalLink);

  const defaultExpiryDate =
    settings && settings.defaultAdsExpiryDate && dayjs().add(settings.defaultAdsExpiryDate, "day").toDate();

  const handleSelectLinkType = (e: any) => {
    setLinkType(e.target.value);
  };

  const handleChangeAdId = (e: any) => {
    const typedId = e.target.value.trim();
    setRecordId(typedId);
  };

  const handleChangeExternalLink = (e: any) => {
    const typedExternalLink = e.target.value.trim();
    setExternalLink(typedExternalLink);
  };

  const handleSubmit = async (data: any) => {
    update(
      AppResource.ads,
      ad.id,
      applyUpdateTimestamp({
        ...data,
        url: externalLink,
        deepUrl: adDeepUrl,
        expiryDate: generateTimestamp(dayjs(data.expiryDate).endOf("day").toDate()),
        expired: false,
        type:
          linkType === LinkType.external
            ? null
            : internalType === "club"
            ? AppResource.sportsClubs
            : internalType === "store"
            ? "store"
            : "agent",
      }),
      ad,
      {
        onSuccess() {
          redirect(`/${AppResource.ads}`);
          notify("Ad has been updated", "success");
        },
        onFailure(ex: any) {
          console.log(ex.message);
          notify("Error on updating ad", "error");
        },
      }
    );
  };

  useEffect(() => {
    setRecordId("");
    setExternalLink("");
  }, [linkType, internalType]);

  useEffect(() => {
    const deepUrl = ad.deepUrl;
    const url = ad.url;

    const isExternal = () => !deepUrl && url;

    if (isExternal()) {
      setLinkType(LinkType.external);
      setExternalLink(url);
    } else {
      setLinkType(LinkType.internal);

      const splittedUrl = deepUrl.split("/");
      setRecordId(splittedUrl[splittedUrl.length - 1]);
    }
  }, [ad]);

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.adsEdit}>
        <FormWithRedirect
          {...props}
          save={handleSubmit}
          redirect={`/${AppResource.ads}`}
          render={(formProps) => (
            <form style={disabledStyles}>
              <CustomCard title="Ad Link">
                <Box display="flex" alignItems="center" mt={2} mr={4}>
                  <Box display="flex" alignItems="flex-end">
                    <Typography variant="body2" color="textSecondary">
                      Link Type
                    </Typography>
                    <Box mx={1}>
                      <Select value={linkType} label="Link Type" onChange={handleSelectLinkType}>
                        <MenuItem value={LinkType.internal}>Internal</MenuItem>
                        <MenuItem value={LinkType.external}>External</MenuItem>
                      </Select>
                    </Box>
                    {linkType === LinkType.internal && (
                      <Box mx={1}>
                        <Select value={internalType} label="Link Type" onChange={handleChangeInternalType}>
                          <MenuItem value={"club"}>Club</MenuItem>
                          <MenuItem value={"store"}>Brand</MenuItem>
                          <MenuItem value={"place"}>Place</MenuItem>
                        </Select>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box my={2}>
                  {linkType === LinkType.internal ? (
                    <>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label={
                          internalType === "club"
                            ? "Sports club ID"
                            : internalType === "store"
                            ? "Brand ID"
                            : "Place ID"
                        }
                        value={recordId}
                        onChange={handleChangeAdId}
                        error={!isValidAdRecord}
                        helperText={adError}
                      />
                      {adRecord && (
                        <Box p={1}>
                          <Typography variant="body2" color="textSecondary">
                            {`${
                              internalType === "club"
                                ? "Club Title"
                                : internalType === "store"
                                ? "Brand Title"
                                : "Place Title"
                            }: ${internalType === "place" ? adRecord.title.en : adRecord.displayName.en}`}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label="External Link"
                        value={externalLink}
                        onChange={handleChangeExternalLink}
                        error={!isValidExternalLink}
                        helperText={externalLinkError}
                      />
                    </>
                  )}
                </Box>
              </CustomCard>

              <CustomCard title="Expiry Date">
                <DateInput
                  source="expiryDate"
                  variant="outlined"
                  validate={[required(), isDateIsOrAfterToday()]}
                  defaultValue={defaultExpiryDate}
                />
              </CustomCard>

              <CustomCard title="Media">
                <ImageUploadInput source="image" validate={[required(), validateFileSize("image")]} />
              </CustomCard>

              <CustomToolbar
                {...formProps}
                formName={FormGroupNames.adsEdit}
                customErrors={{
                  link: externalLinkError,
                  recordId: adError,
                  isLinkExists:
                    !externalLink && !adDeepUrl ? `please provide either sports club id, or external link` : "",
                }}
              />
            </form>
          )}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const AdsEdit: FC<EditProps> = (props) => {
  return (
    <Edit {...props} component="section">
      <CustomForm />
    </Edit>
  );
};

export default AdsEdit;
